import { Token } from 'vvs-sdk'
import createDependableResource from 'utils/zustand/createDependableResource'
import reduxStore, { AppState } from 'state'
import { onReduxFarmChange } from 'state/farms/onChange'
import deserializeFarm from 'state/farms/deserializeFarm'
import { useActiveFarmsPeriodicFetching, useFinishedFarmsPeriodicFetching } from 'state/farms/farmsPeriodicFetching'
import { SerializedFarm, DeserializedFarm } from 'state/types'
import { immediatePromise } from 'utils/waitPromise'

interface DeserializedFarmsInfo {
  farms: DeserializedFarm[]
  farmsV1FromPid: Record<number, DeserializedFarm>
  farmsV2FromPid: Record<number, DeserializedFarm>
}

type Pid = number
const cachedFarmDatas: Record<Pid, [serializedFarm: SerializedFarm, deserializedFarm: DeserializedFarm]> = {}

const NOOP = () => undefined

/**
 * NOTE: this does not guarantee farm data (will be) loaded
 * please consider other hooks/functions in state/farms/useDeserializedFarms.ts
 */
export const useDeserializedFarms = createDependableResource<DeserializedFarmsInfo>({
  initValue: {
    farms: [],
    farmsV1FromPid: {},
    farmsV2FromPid: {},
  },
  getValue: () => {
    const farms = reduxStore.getState().farms.data.map((farmData) => {
      const cachedFarmData = cachedFarmDatas[farmData.pid]
      if (cachedFarmData && cachedFarmData[0] === farmData) {
        return cachedFarmData[1]
      }
      const deserialized = deserializeFarm(farmData)
      cachedFarmDatas[farmData.pid] = [farmData, deserialized]

      return deserialized
    })
    return {
      farms,
      farmsV1FromPid: Object.fromEntries(farms.filter((f) => f.version === 1).map((f) => [f.pid, f])),
      farmsV2FromPid: Object.fromEntries(farms.filter((f) => f.version === 2).map((f) => [f.pid, f])),
    }
  },
  onInit: ({ refresh }) => {
    let currentFarmData: AppState['farms']['data']
    onReduxFarmChange((reduxFarmsState) => {
      const prevFarmData = currentFarmData
      currentFarmData = reduxFarmsState.data

      if (currentFarmData !== prevFarmData) {
        refresh()
      }
    })
  },
  dependResources: [],
  dbgName: 'DeserializedFarms',
})

export const selectDeserializedFarm = (
  deserializedFarmsValue: DeserializedFarmsInfo,
  pid: number,
  version: number,
): DeserializedFarm => {
  switch (version) {
    case 1:
      return deserializedFarmsValue.farmsV1FromPid[pid]
    case 2:
      return deserializedFarmsValue.farmsV2FromPid[pid]
    default:
      return null
  }
}

export const getDeserializedFarm = (pid: number, version: number): DeserializedFarm => {
  return selectDeserializedFarm(useDeserializedFarms.getState().value, pid, version)
}

export const getLoadedDeserializedFarmFromPid = async (pid: number, version: number): Promise<DeserializedFarm> => {
  const farm = getDeserializedFarm(pid, version)
  if (farm.loaded) return farm

  await makeFarmRefreshOnce(farm)
  await immediatePromise()
  return getDeserializedFarm(pid, version)
}

export const useDeserializedFarmFromPid = (pid: number, version: number): DeserializedFarm => {
  return useDeserializedFarms((s) => {
    const farm = selectDeserializedFarm(s.value, pid, version)

    makeFarmRefreshOnce(farm).catch(NOOP) // prevent too much uncaught error report
    return farm
  })
}

export const useDeserializedFarmFromLpPair = (token: Token, quoteToken: Token): DeserializedFarm => {
  return useDeserializedFarms((s) => {
    const farm = s.value.farms.find((f) => f.token.symbol === token.symbol && f.quoteToken.symbol === quoteToken.symbol)

    makeFarmRefreshOnce(farm).catch(NOOP) // prevent too much uncaught error report
    return farm
  })
}

export const useDeserializedFarmFromLpSymbol = (lpSymbol: string): DeserializedFarm => {
  return useDeserializedFarms((s) => {
    const farm = s.value.farms.find((f) => f.lpSymbol === lpSymbol)

    makeFarmRefreshOnce(farm).catch(NOOP) // prevent too much uncaught error report
    return farm
  })
}

const makeFarmRefreshOnce = async (farm?: DeserializedFarm) => {
  if (!farm) return
  if (!farm.isFinished) {
    await useActiveFarmsPeriodicFetching.untilRefreshedOnce()
  } else {
    await useFinishedFarmsPeriodicFetching.untilRefreshedOnce()
  }
}
